import { useEffect, useState } from "react";
import { Copy, Download, ExternalLink, Film, ImageIcon, Palette, Type, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ReferralToolkit from "@/components/dashboard/ReferralToolkit";

const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

function Lightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div
      onClick={onClose}
      data-testid="lightbox-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-6"
    >
      <button onClick={onClose} className="absolute right-5 top-5 rounded-full bg-zinc-900 p-2 text-white hover:bg-zinc-800" aria-label="Close">
        <X className="h-5 w-5" />
      </button>
      <img src={src} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
    </div>
  );
}

function BrandKit({ items, resolve = (t) => t }) {
  const [zoom, setZoom] = useState(null);
  if (items.length === 0) return null;
  const logos = items.filter((i) => i.file_type === "brand_logo");
  const colors = items.filter((i) => i.file_type === "brand_color");
  const fonts = items.filter((i) => i.file_type === "brand_font");
  return (
    <div className="rounded-2xl border border-[#A855F7]/30 bg-gradient-to-br from-[#A855F7]/5 via-zinc-950 to-zinc-950 p-6" data-testid="brand-kit-section">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-xl font-bold">Brand kit</h3>
        <p className="text-xs text-zinc-500">Official assets — for promotional use only</p>
      </div>

      {logos.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Logos</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {logos.map((l) => (
              <div key={l.id} data-testid={`bk-logo-${l.id}`} className="group rounded-lg border border-zinc-800 bg-white p-3">
                <button onClick={() => setZoom(fileUrl(l.file_path))} className="block w-full">
                  <img src={fileUrl(l.file_path)} alt={l.name} className="mx-auto h-16 w-full object-contain" />
                </button>
                <div className="mt-2 flex items-center justify-between text-xs text-zinc-700">
                  <span className="truncate font-semibold">{l.name}</span>
                  <a href={fileUrl(l.file_path)} download={l.original_filename} className="inline-flex items-center gap-1 rounded bg-zinc-900 px-2 py-1 text-[10px] font-bold text-white hover:bg-[#A855F7]">
                    <Download className="h-3 w-3" /> PNG
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {colors.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Brand colors</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {colors.map((c) => {
              const hex = (c.meta?.hex || "").toUpperCase();
              return (
                <button
                  key={c.id}
                  onClick={() => copyText(hex)}
                  data-testid={`bk-color-${c.id}`}
                  className="group flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-left hover:border-[#A855F7]/60"
                >
                  <span className="h-10 w-10 shrink-0 rounded-md border border-zinc-700" style={{ backgroundColor: hex }} />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{c.name}</p>
                    <p className="font-mono text-xs text-[#A855F7]">{hex}</p>
                  </div>
                  <Copy className="h-3 w-3 shrink-0 text-zinc-500 group-hover:text-white" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {fonts.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Typography</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {fonts.map((f) => (
              <div key={f.id} data-testid={`bk-font-${f.id}`} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                <p className="text-xs text-zinc-500">{f.name}</p>
                <p className="mt-1 text-2xl text-white" style={{ fontFamily: f.meta?.font_family }}>{f.meta?.font_family || "Sans"}</p>
                {f.description && <p className="mt-1 whitespace-pre-wrap text-xs text-zinc-500">{resolve(f.description)}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </div>
  );
}

function ContentLibrary({ items, resolve = (t) => t }) {
  const [zoom, setZoom] = useState(null);
  if (items.length === 0) return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
      <h3 className="font-display text-xl font-bold">Content library</h3>
      <p className="mt-5 text-sm text-zinc-500">No assets uploaded yet — your cohort manager will add them soon.</p>
    </div>
  );

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6" data-testid="content-library-section">
      <h3 className="font-display text-xl font-bold">Content library</h3>
      <p className="mt-1 text-sm text-zinc-500">Reshare these on your channels. Submit the live link in the Submit tab to claim reshare points.</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="assets-list">
        {items.map((f) => {
          const isImage = f.content_type?.startsWith("image/") || ["image", "logo", "banner", "template"].includes(f.file_type);
          const isVideo = f.file_type === "video" || f.content_type?.startsWith("video/");
          return (
            <div key={f.id} data-testid={`asset-${f.id}`} className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900">
              {isImage && f.file_path && (
                <button onClick={() => setZoom(fileUrl(f.file_path))} className="block w-full">
                  <img src={fileUrl(f.file_path)} alt={f.name} className="h-44 w-full bg-zinc-950 object-contain" />
                </button>
              )}
              {isVideo && (
                <div className="bg-black">
                  {f.file_path ? (
                    <video src={fileUrl(f.file_path)} controls className="h-44 w-full bg-black object-contain" />
                  ) : (
                    <div className="flex h-44 items-center justify-center text-xs text-zinc-500">
                      <Film className="mr-2 h-4 w-4" /> Watch on platform →
                    </div>
                  )}
                </div>
              )}
              <div className="p-3">
                <p className="font-display font-semibold text-white">{f.name}</p>
                {f.description && <p className="mt-1 whitespace-pre-wrap text-xs text-zinc-400">{resolve(f.description)}</p>}
                {f.description && /\[|\{/.test(f.description) && (
                  <button onClick={() => { navigator.clipboard.writeText(resolve(f.description)); }} className="mt-2 inline-flex items-center gap-1 rounded border border-zinc-700 px-2 py-0.5 text-[10px] text-zinc-300 hover:border-zinc-500" title="Copy this caption with your link">
                    <Copy className="h-3 w-3" /> Copy caption
                  </button>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {isVideo && f.external_url && (
                    <a href={f.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded border border-[#A855F7]/60 bg-[#A855F7]/10 px-2 py-1 text-[11px] font-semibold text-[#E9D5FF] hover:bg-[#A855F7]/20" title="Quick share: Add to your Instagram story directly from our post">
                      <ExternalLink className="h-3 w-3" /> Quick share
                    </a>
                  )}
                  {f.file_path && (
                    <a href={fileUrl(f.file_path)} download={f.original_filename} className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-[11px] font-semibold text-zinc-200 hover:border-zinc-500" title={isVideo ? "Download and repost as your own Reel" : "Download"}>
                      <Download className="h-3 w-3" /> {isVideo ? "Download & repost" : "Download"}
                    </a>
                  )}
                </div>
                {isVideo && (
                  <p className="mt-2 text-[10px] text-zinc-500">
                    {f.external_url ? "Quick share posts NoobSync's version. Download & repost lets you post it as your own Reel." : "Download the MP4 and repost as your own Reel."}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </div>
  );
}

function CommunityOriginals({ items, resolve = (t) => t }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/[0.04] via-zinc-950 to-zinc-950 p-6" data-testid="community-originals">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="font-display text-xl font-bold">Community originals</h3>
          <p className="mt-1 text-sm text-zinc-500">Approved posts from your cohort. Reshare → submit your repost URL → earn reshare points.</p>
        </div>
        <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">
          {items.length} approved
        </span>
      </div>
      <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="community-originals-list">
        {items.map((s) => {
          const isVideo = s.file_type === "video";
          const preview = s.file_path ? fileUrl(s.file_path) : null;
          const hasLive = s.is_published_live;
          return (
            <div key={s.id} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 hover:border-emerald-400/40 transition" data-testid={`comm-${s.id}`}>
              {preview && !isVideo && (
                <img src={preview} alt="" className="mb-3 h-32 w-full rounded border border-zinc-800 object-cover" />
              )}
              <div className="flex items-start justify-between gap-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 inline-flex items-center gap-1">
                  {isVideo ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />} {s.file_type}
                </p>
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${hasLive ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                  {hasLive ? "✓ live" : "draft"}
                </span>
              </div>
              <p className="mt-1 font-display font-semibold text-white line-clamp-2">{s.name}</p>
              {s.description && <p className="mt-1 whitespace-pre-wrap text-xs text-zinc-500">{resolve(s.description)}</p>}
              {s.external_url && (
                <a href={s.external_url} target="_blank" rel="noreferrer" className="mt-2 block truncate text-[11px] text-[#A855F7] hover:underline">
                  {s.external_url}
                </a>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {s.external_url && (
                  <a
                    href={s.external_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded bg-emerald-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-emerald-400"
                    data-testid={`comm-open-${s.id}`}
                  >
                    <ExternalLink className="h-3 w-3" /> {hasLive ? "Open & reshare" : "Open draft"}
                  </a>
                )}
                {preview && (
                  <a
                    href={preview}
                    download
                    className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2.5 py-1.5 text-xs hover:border-emerald-400 hover:text-emerald-300"
                    data-testid={`comm-download-${s.id}`}
                  >
                    <Download className="h-3 w-3" /> Download asset
                  </a>
                )}
                {s.external_url && (
                  <button
                    onClick={() => copyText(s.external_url)}
                    className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2.5 py-1.5 text-xs hover:border-zinc-500"
                    data-testid={`comm-copy-${s.id}`}
                  >
                    <Copy className="h-3 w-3" /> Copy URL
                  </button>
                )}
              </div>
              {!hasLive && (
                <p className="mt-2 text-[10px] text-amber-300/80">
                  Creator hasn&apos;t posted publicly yet — download the asset and reshare with your own post, or wait for the live URL.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function ResourcesTab({ referralCode, referralLink, ambassadorName }) {
  const [items, setItems] = useState([]);
  const [customPlaceholders, setCustomPlaceholders] = useState([]);
  useEffect(() => {
    api.get("/ambassador/resources").then((r) => setItems(r.data.items));
    api.get("/site-settings").then((r) => setCustomPlaceholders(r.data.caption_placeholders || [])).catch(() => {});
  }, []);

  const captionTemplates = items.filter((i) => i.file_type === "caption");
  const brandKit = items.filter((i) => i.category === "brand_kit");
  const community = items.filter((i) => i.category === "community_originals");
  const library = items.filter((i) => i.category !== "brand_kit" && i.category !== "community_originals" && i.file_type !== "caption");

  // Replace placeholders in caption templates. Built-in tokens are personalized
  // per ambassador; admin-defined custom placeholders resolve to fixed values.
  // Both [KEY], {KEY} and {{KEY}} syntaxes are supported.
  const buildCaption = (t) => {
    const tokens = {
      REF_LINK: referralLink || "",
      LINK: referralLink || "",
      REF: referralCode || "",
      CODE: referralCode || "",
      NAME: ambassadorName || "",
    };
    (customPlaceholders || []).forEach((p) => { if (p.key) tokens[p.key.toUpperCase()] = p.value || ""; });
    let out = t || "";
    Object.entries(tokens).forEach(([k, v]) => {
      const re = new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}|\\{\\s*${k}\\s*\\}|\\[\\s*${k}\\s*\\]`, "gi");
      out = out.replace(re, v);
    });
    return out;
  };

  const qrPosters = items.filter((i) => i.file_path && i.qr_overlay && i.qr_overlay.enabled);
  const [sub, setSub] = useState("posters");

  const tabs = [
    { key: "posters", label: "Posters & QR" },
    { key: "captions", label: "Captions", count: captionTemplates.length },
    { key: "community", label: "Community originals", count: community.length, show: community.length > 0 },
    { key: "library", label: "Content library", count: library.length },
    { key: "brand", label: "Brand kit", show: brandKit.length > 0 },
  ].filter((t) => t.show !== false);

  return (
    <div className="space-y-6">
      <div className="flex gap-1 overflow-x-auto rounded-xl border border-zinc-900 bg-zinc-950 p-1" data-testid="resources-subnav">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`res-subtab-${t.key}`}
            onClick={() => setSub(t.key)}
            className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider transition ${sub === t.key ? "bg-[#A855F7] text-white" : "text-zinc-400 hover:text-zinc-200"}`}
          >
            {t.label}{t.count ? ` (${t.count})` : ""}
          </button>
        ))}
      </div>

      {sub === "posters" && <ReferralToolkit referralCode={referralCode} posters={qrPosters} />}

      {sub === "brand" && <BrandKit items={brandKit} resolve={buildCaption} />}

      {sub === "captions" && (
        <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
          <h3 className="font-display text-xl font-bold">Ready-to-post captions</h3>
          <p className="mt-1 text-sm text-zinc-500">Templates pre-filled with your referral link.</p>
          {captionTemplates.length === 0 ? (
            <p className="mt-5 text-sm text-zinc-500">No captions yet — your cohort manager will add them soon.</p>
          ) : (
            <div className="mt-5 grid gap-3 md:grid-cols-2" data-testid="captions-list">
              {captionTemplates.map((c) => {
                const text = buildCaption(c.caption_text || "");
                return (
                  <div key={c.id} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#A855F7]">{c.name}</p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-300">{text}</p>
                    <button onClick={() => copyText(text)} className="mt-3 inline-flex items-center gap-1 rounded border border-zinc-700 px-3 py-1 text-xs hover:border-zinc-500">
                      <Copy className="h-3 w-3" /> Copy caption
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {sub === "community" && <CommunityOriginals items={community} resolve={buildCaption} />}

      {sub === "library" && <ContentLibrary items={library} resolve={buildCaption} />}

      <div className="rounded-md border border-zinc-800 bg-zinc-900/40 px-4 py-3 text-xs text-zinc-500">
        <Palette className="mr-1 inline h-3 w-3" /> Brand assets &amp; library content are licensed to you for promotional use during active program participation only — see the <Link to="/terms#asset-license" className="text-[#A855F7] hover:underline">Terms &amp; Conditions</Link>.
      </div>
    </div>
  );
}
